import React, { useCallback, useMemo, useState } from 'react'
import {
  AuditsSVG,
  CircleCheckSVG,
  CompanyInfoSVG,
  ControlsSVG,
  EvidenceSVG,
  GrcSVG,
  KbDocumentSVG,
  PoliciesSVG,
  RisksSVG,
  ServicesSVG,
} from 'src/components/Icons/Basic'
import { faGlobe } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ModalContainer } from 'src/components/ModalForms/ModalContainer'
import {
  CloseButton,
  StandardButton,
  StandardButtonSize,
  StandardButtonVariant,
} from 'src/components/Reusable/Buttons'
import { ChatContextSourceType } from '@trustero/trustero-api-web/lib/chatcontext/chatcontext_pb'
import { Tooltip } from 'src/components/Reusable/Tooltip'
import {
  ChatContext,
  ChatContextGroup,
  ChatContextSelection,
  ChatContextType,
  HiddenChatContextType,
  useGetSummary,
} from 'src/components/Reusable/ChatContext/useChatContext.hooks'
import {
  getDefaultChatContext,
  getFilteredDefaultChatContext,
} from 'src/components/Reusable/ChatContext/chatContext.utils'
import {
  ContextTypeSelectionModalContainer,
  ContextTypeSelectionModalTitle,
  ContextTypeSelectorButton,
  ContextTypeSelectorButtonContainer,
  ContextTypeSelectorContainer,
  ContextTypeSelectorTextContainer,
  ContextTypeSelectorTitle,
} from '../chatContextPanel.styles'
import { CompanyModal } from '../togglecontexts/CompanyModal'
import { ControlModal } from '../control/ControlModal'
import { EvidenceModal } from '../evidence/EvidenceModal'
import { KbModal } from '../kb/KbModal'
import { PolicyModal } from '../policy/PolicyModal'
import { StatusPanel, Summary } from '../chatContextPanel.components'
import { DocumentGroupModal } from '../documentgroup/DocumentGroupModal'
import { UserModal } from '../togglecontexts/User'
import { ExaminationTestModal } from '../togglecontexts/ExaminationTest'
import { VendorManagementModal } from '../togglecontexts/VendorManagement'
import { RiskRegisterModal } from '../togglecontexts/RiskRegister'
import { ReceptorsModal } from '../togglecontexts/Receptors'
import { DocumentManagementModal } from '../togglecontexts/DocumentManagement'
import { AuditsModal } from '../togglecontexts/Audits'
import { SupportKnowledgeModal } from '../togglecontexts/SupportKnowledge'

export const ToolCallContextTypes = new Set([
  ChatContextSourceType.USERS,
  ChatContextSourceType.EXAMINATION_AND_TEST,
  ChatContextSourceType.VENDORS,
  ChatContextSourceType.RISKS,
  ChatContextSourceType.RECEPTORS,
  ChatContextSourceType.DMS,
  ChatContextSourceType.SUPPORT_KNOWLEDGE,
])

export type ContextGroupDetails = {
  groupType: ChatContextGroup
  label: string
  icon: React.ReactNode
  types: ChatContextType[]
}

export const ContextGroups: ContextGroupDetails[] = [
  {
    groupType: 'COMPANY',
    label: 'Company Info & Scope',
    icon: <CompanyInfoSVG />,
    types: [ChatContextSourceType.COMPANY],
  },
  {
    groupType: 'KNOWLEDGE_BASE',
    label: 'Knowledge Base',
    icon: <GrcSVG />,
    types: [
      ChatContextSourceType.KNOWLEDGE_BASE,
      ChatContextSourceType.DOCUMENT_GROUP,
      ChatContextSourceType.SUPPORT_KNOWLEDGE,
    ],
  },
  {
    groupType: 'COMPLIANCE',
    label: 'Compliance',
    icon: <PoliciesSVG />,
    types: [
      ChatContextSourceType.POLICY,
      ChatContextSourceType.CONTROL,
      ChatContextSourceType.EVIDENCE,
      ChatContextSourceType.EXAMINATION_AND_TEST,
      ChatContextSourceType.AUDIT,
    ],
  },
  {
    groupType: 'RISK',
    label: 'Risk',
    icon: <RisksSVG />,
    types: [ChatContextSourceType.RISKS, ChatContextSourceType.VENDORS],
  },
  {
    groupType: 'INTEGRATIONS',
    label: 'Integrations',
    icon: <ServicesSVG />,
    types: [ChatContextSourceType.RECEPTORS, ChatContextSourceType.DMS],
  },
  {
    groupType: 'USERS',
    label: 'Users',
    icon: <CompanyInfoSVG />,
    types: [ChatContextSourceType.USERS],
  },
]

export type ChatContextTypeDetails = {
  type: ChatContextType
  icon: React.ReactNode
  label: string
}

export type AllChatContextTypeDetails = {
  type: HiddenChatContextType | ChatContextType
  icon: React.ReactNode
  label: string
}

export const SupportedContexts: ChatContextTypeDetails[] = [
  {
    type: ChatContextSourceType.DOCUMENT_GROUP,
    icon: <KbDocumentSVG />,
    label: 'Knowledge Base Documents',
  },
  {
    type: ChatContextSourceType.KNOWLEDGE_BASE,
    icon: <GrcSVG />,
    label: 'GRC Q&A Knowledge Base',
  },
  {
    type: ChatContextSourceType.POLICY,
    icon: <PoliciesSVG />,
    label: 'Policies',
  },
  {
    type: ChatContextSourceType.CONTROL,
    icon: <ControlsSVG />,
    label: 'Controls',
  },
  {
    type: ChatContextSourceType.COMPANY,
    icon: <CompanyInfoSVG />,
    label: 'Company Info',
  },
  {
    type: ChatContextSourceType.EVIDENCE,
    icon: <EvidenceSVG />,
    label: 'Evidence',
  },
  {
    type: ChatContextSourceType.USERS,
    icon: <CompanyInfoSVG />,
    label: 'Users',
  },
  {
    type: ChatContextSourceType.EXAMINATION_AND_TEST,
    icon: <ControlsSVG />,
    label: 'Control Tests',
  },
  {
    type: ChatContextSourceType.VENDORS,
    icon: <CompanyInfoSVG />,
    label: 'Vendors',
  },
  {
    type: ChatContextSourceType.RISKS,
    icon: <RisksSVG />,
    label: 'Risk Register',
  },
  {
    type: ChatContextSourceType.RECEPTORS,
    icon: <ServicesSVG />,
    label: 'Receptors',
  },
  {
    type: ChatContextSourceType.DMS,
    icon: <ServicesSVG />,
    label: 'Document Management Systems',
  },
  {
    type: ChatContextSourceType.AUDIT,
    icon: <AuditsSVG />,
    label: 'Audits',
  },
  {
    type: ChatContextSourceType.SUPPORT_KNOWLEDGE,
    icon: <FontAwesomeIcon icon={faGlobe} style={{ marginBottom: '-2px' }} />,
    label: 'Support Documentation',
  },
]

export const AllContexts: AllChatContextTypeDetails[] = [
  ...SupportedContexts,
  {
    type: ChatContextSourceType.ARTIFACT_DOCUMENT,
    icon: <KbDocumentSVG />,
    label: 'Artifact Documents',
  },
  {
    type: ChatContextSourceType.CHAT_UPLOAD_DOCUMENT,
    icon: <KbDocumentSVG />,
    label: 'Chat Upload Documents',
  },
  {
    type: ChatContextSourceType.CONTROL_LIST,
    icon: <ControlsSVG />,
    label: 'Controls List',
  },
  {
    type: ChatContextSourceType.POLICY_LIST,
    icon: <PoliciesSVG />,
    label: 'Policies List',
  },
  {
    type: ChatContextSourceType.TRUST_PORTAL_CONTROL,
    icon: <ControlsSVG />,
    label: 'Trust Portal Controls',
  },
  {
    type: ChatContextSourceType.TRUST_PORTAL_FAQ,
    icon: <GrcSVG />,
    label: 'Trust Portal FAQs',
  },
  {
    type: ChatContextSourceType.TRUST_PORTAL_UPDATE,
    icon: <GrcSVG />,
    label: 'Trust Portal Updates',
  },
]

type ContextEditState = {
  isUpdatingContext: boolean
  errorUpdatingContext: string | null
}

export type ContextTypeSelectionModalProps = {
  show: boolean
  hide: () => void
  context: ChatContext
  setContext: (context: ChatContext) => void
  chatContextId?: string
  groupType?: ChatContextGroup
  title?: string
  includeToolCalls?: boolean
} & ContextEditState

export const ContextTypeSelectionModal = ({
  show,
  hide,
  context,
  setContext,
  chatContextId,
  groupType,
  title = 'Change Context',
  includeToolCalls = false,
  isUpdatingContext = false,
  errorUpdatingContext = null,
}: ContextTypeSelectionModalProps): JSX.Element => {
  const [selectedContextType, setSelectedContextType] = useState<
    ChatContextType | undefined
  >()

  const summary = useGetSummary({ context, chatContextId, shortFormat: true })

  const handleContextTypeChange = (type: ChatContextType) => {
    setSelectedContextType(type)
  }

  const handleClose = useCallback(() => {
    setSelectedContextType(undefined)
    hide()
  }, [hide])

  const onBack = useCallback(() => {
    setSelectedContextType(undefined)
  }, [])

  const groupedTypes = useMemo<Set<ChatContextType>>(() => {
    if (groupType) {
      return new Set(
        ContextGroups.find((group) => group.groupType === groupType)?.types ??
          [],
      )
    }
    return new Set()
  }, [groupType])

  const supportedContexts = useMemo<ChatContextTypeDetails[]>(() => {
    if (groupedTypes.size > 0) {
      return Array.from(groupedTypes)
        .map((type: ChatContextType) =>
          SupportedContexts.find(
            (context) =>
              context.type === type &&
              (includeToolCalls || !ToolCallContextTypes.has(type)),
          ),
        )
        .filter((context) => !!context) as ChatContextTypeDetails[]
    } else {
      return SupportedContexts.filter(
        (context) =>
          includeToolCalls || !ToolCallContextTypes.has(context.type),
      )
    }
  }, [groupedTypes, includeToolCalls])

  return (
    <>
      <ModalContainer
        show={show && selectedContextType === undefined}
        hide={handleClose}
        size="lg"
      >
        <CloseButton
          position="absolute"
          top="s"
          right="s"
          onClick={(e: React.MouseEvent) => {
            e.preventDefault()
            hide()
          }}
        />
        <ContextTypeSelectionModalContainer>
          <ContextTypeSelectionModalTitle>
            {title}
          </ContextTypeSelectionModalTitle>
          <ContextTypeSelectorContainer>
            {supportedContexts.map(({ type, icon, label }) => (
              <ContextTypeSelector
                key={type}
                icon={icon}
                label={label}
                summary={summary[type]}
                onClick={() => handleContextTypeChange(type)}
              />
            ))}
          </ContextTypeSelectorContainer>
          <QuickContextActions
            context={context}
            setContext={setContext}
            groupedTypes={groupedTypes}
            isUpdatingContext={isUpdatingContext}
            errorUpdatingContext={errorUpdatingContext}
          />
        </ContextTypeSelectionModalContainer>
      </ModalContainer>
      <ContextTypeModal
        contextType={selectedContextType}
        close={handleClose}
        onBack={onBack}
        context={context}
        setContext={setContext}
      />
    </>
  )
}

export type ContextTypeSelectionButtonProps = {
  onClick: () => void
  icon: React.ReactNode
  label: string
  summary: string | string[]
  singleSelect?: boolean
  withRadius?: boolean
}

export const ContextTypeSelector = ({
  icon,
  onClick,
  label,
  summary,
  singleSelect,
  withRadius,
}: ContextTypeSelectionButtonProps): JSX.Element => {
  return (
    <Tooltip
      id={`tooltip-edit-context-modal-${label}`}
      trigger={['hover', 'click']}
      tooltipBody={`Edit context for ${label}`}
      placement="top"
    >
      <ContextTypeSelectorButton
        onClick={onClick}
        $singleSelect={singleSelect}
        $withRadius={withRadius}
      >
        {singleSelect ? <CircleCheckSVG /> : icon}
        <ContextTypeSelectorTextContainer>
          <ContextTypeSelectorTitle>{label}</ContextTypeSelectorTitle>
          <Summary summary={summary} centerAligned />
        </ContextTypeSelectorTextContainer>
      </ContextTypeSelectorButton>
    </Tooltip>
  )
}

export type ContextTypeModalProps = {
  contextType?: ChatContextType
  close: () => void
  onBack?: () => void
  context: ChatContext
  setContext: (context: ChatContext) => void
}

export const ContextTypeModal = ({
  contextType,
  close,
  onBack,
  context,
  setContext,
}: ContextTypeModalProps): JSX.Element => {
  return (
    <>
      <PolicyModal
        show={contextType === ChatContextSourceType.POLICY}
        hide={close}
        onBack={onBack}
        context={context}
        setContext={setContext}
      />
      <KbModal
        show={contextType === ChatContextSourceType.KNOWLEDGE_BASE}
        hide={close}
        onBack={onBack}
        context={context}
        setContext={setContext}
      />
      <EvidenceModal
        show={contextType === ChatContextSourceType.EVIDENCE}
        hide={close}
        onBack={onBack}
        context={context}
        setContext={setContext}
      />
      <ControlModal
        show={contextType === ChatContextSourceType.CONTROL}
        hide={close}
        onBack={onBack}
        context={context}
        setContext={setContext}
      />
      <CompanyModal
        show={contextType === ChatContextSourceType.COMPANY}
        hide={close}
        onBack={onBack}
        context={context}
        setContext={setContext}
      />
      <DocumentGroupModal
        show={contextType === ChatContextSourceType.DOCUMENT_GROUP}
        hide={close}
        onBack={onBack}
        context={context}
        setContext={setContext}
      />
      <UserModal
        show={contextType === ChatContextSourceType.USERS}
        hide={close}
        onBack={onBack}
        context={context}
        setContext={setContext}
      />
      <ExaminationTestModal
        show={contextType === ChatContextSourceType.EXAMINATION_AND_TEST}
        hide={close}
        onBack={onBack}
        context={context}
        setContext={setContext}
      />
      <VendorManagementModal
        show={contextType === ChatContextSourceType.VENDORS}
        hide={close}
        onBack={onBack}
        context={context}
        setContext={setContext}
      />
      <RiskRegisterModal
        show={contextType === ChatContextSourceType.RISKS}
        hide={close}
        onBack={onBack}
        context={context}
        setContext={setContext}
      />
      <ReceptorsModal
        show={contextType === ChatContextSourceType.RECEPTORS}
        hide={close}
        onBack={onBack}
        context={context}
        setContext={setContext}
      />
      <DocumentManagementModal
        show={contextType === ChatContextSourceType.DMS}
        hide={close}
        onBack={onBack}
        context={context}
        setContext={setContext}
      />
      <AuditsModal
        show={contextType === ChatContextSourceType.AUDIT}
        hide={close}
        onBack={onBack}
        context={context}
        setContext={setContext}
      />
      <SupportKnowledgeModal
        show={contextType === ChatContextSourceType.SUPPORT_KNOWLEDGE}
        hide={close}
        onBack={onBack}
        context={context}
        setContext={setContext}
      />
    </>
  )
}

export type QuickActionsProps = {
  allIncluded: boolean
  allExcluded: boolean
  includeAllContent: () => void
  excludeAllContent: () => void
  layout?: 'horizontal' | 'vertical'
}

export const QuickActions = ({
  allIncluded,
  allExcluded,
  includeAllContent,
  excludeAllContent,
  layout = 'horizontal',
}: QuickActionsProps): JSX.Element => {
  return (
    <ContextTypeSelectorContainer>
      {layout === 'horizontal' && (
        <ContextTypeSelectorTitle>Quick Actions</ContextTypeSelectorTitle>
      )}
      <ContextTypeSelectorButtonContainer>
        {!allIncluded && (
          <StandardButton
            onClick={includeAllContent}
            variant={StandardButtonVariant.SECONDARY}
            buttonSize={StandardButtonSize.SMALL}
          >
            {layout === 'horizontal' ? 'Include All Content' : 'Include All'}
          </StandardButton>
        )}
        {!allExcluded && (
          <StandardButton
            onClick={excludeAllContent}
            variant={StandardButtonVariant.SECONDARY}
            buttonSize={StandardButtonSize.SMALL}
          >
            {layout === 'horizontal' ? 'Exclude All Content' : 'Exclude All'}
          </StandardButton>
        )}
      </ContextTypeSelectorButtonContainer>
    </ContextTypeSelectorContainer>
  )
}

export type QuickContextActionsProps = {
  context: ChatContext
  setContext: (context: ChatContext) => void
  groupedTypes?: Set<ChatContextType>
  layout?: 'horizontal' | 'vertical'
} & ContextEditState

export const QuickContextActions = ({
  context,
  setContext,
  groupedTypes = new Set(),
  layout = 'horizontal',
  isUpdatingContext,
  errorUpdatingContext,
}: QuickContextActionsProps): JSX.Element => {
  const checkAllIncluded = useCallback(
    (value: boolean) => {
      if (groupedTypes.size > 0) {
        return Array.from(groupedTypes).every(
          (type: ChatContextType) => context[type].includeAll === value,
        )
      } else {
        const sourceSelections = Object.values(context).filter(
          (source) => typeof source === 'object' && 'includeAll' in source,
        ) as ChatContextSelection[]
        return sourceSelections.every((source) => source.includeAll === value)
      }
    },
    [context, groupedTypes],
  )

  const allIncluded = useMemo(() => checkAllIncluded(true), [checkAllIncluded])

  const allExcluded = useMemo(() => checkAllIncluded(false), [checkAllIncluded])

  const updateContent = useCallback(
    (includeAll: boolean) => {
      if (groupedTypes.size > 0) {
        setContext(
          getFilteredDefaultChatContext(context, includeAll, groupedTypes),
        )
      } else {
        setContext(getDefaultChatContext(includeAll))
      }
    },
    [setContext, groupedTypes, context],
  )

  const includeAllContent = useCallback(() => {
    updateContent(true)
  }, [updateContent])

  const excludeAllContent = useCallback(() => {
    updateContent(false)
  }, [updateContent])

  return (
    <>
      <QuickActions
        allIncluded={allIncluded}
        allExcluded={allExcluded}
        includeAllContent={includeAllContent}
        excludeAllContent={excludeAllContent}
        layout={layout}
      />
      <StatusPanel
        busy={isUpdatingContext ?? false}
        error={errorUpdatingContext}
      />
    </>
  )
}
